/**
 * SsoConfig API contract shared with the sf-auth-service (SF-3404).
 *
 * Source of truth: servefirstcx/sf-auth-service `src/models/SsoConfig.ts` +
 * `src/routes/ssoConfig.ts` `formatConfig()`. Kept in sync manually — if the
 * backend shape drifts, this file and the form mapping in `SsoConfigForm.tsx`
 * are the two places to update.
 *
 * Secrets: `clientSecret` is always the masked literal "***" on the wire; the
 * encrypted blob (`clientSecretEnc`) is never exposed.
 */

export type SsoProvider = 'oidc' | 'pingfed' | 'pingone';
export type TokenEndpointAuthMethod = 'client_secret_basic' | 'client_secret_post';
export type RoleMatchType = 'exact' | 'regex';
export type ExistingUserRolePolicy = 'preserve' | 'overwrite' | 'flag';

/**
 * SSO existing-user location policy. On each SSO login, when the user
 * already exists in ServeFirst, decides whether to keep their stored
 * locations (`preserve`) or replace them with the IdP-provided locations
 * (`overwrite`). Default: `preserve` (non-destructive — locations are
 * managed in ServeFirst). See sf-auth-service SF-3591.
 */
export type ExistingUserLocationPolicy = 'preserve' | 'overwrite';

export interface RoleToTypeRule {
  match: RoleMatchType;
  value: string;
  type: string;
}

export interface SsoAttributeMapping {
  email?: string;
  roles?: string;
  locations?: string;
  employeeId?: string;
}

export interface SsoConfig {
  id: string;
  name: string;
  companyId: string;
  companyName: string | null;
  domains: string[];
  provider: SsoProvider;
  discoveryUrl: string;
  clientId: string;
  /** Always "***" from GET. Only sent on PATCH when the operator enters a new value. */
  clientSecret: string;
  tokenEndpointAuthMethod: TokenEndpointAuthMethod;
  attributeMapping: SsoAttributeMapping | null;
  roleToType: RoleToTypeRule[];
  existingUserRolePolicy: ExistingUserRolePolicy;
  existingUserLocationPolicy?: ExistingUserLocationPolicy;
  defaultType: string | null;
  pingOneRegion: string | null;
  pingOneEnvironmentId: string | null;
  scopes: string[];
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Masked literal returned by the auth-service for `clientSecret`. We compare
 * against this on PATCH to decide whether to send the field — preserving the
 * stored secret when the operator hasn't touched the input.
 */
export const MASKED_SECRET = '***';
